<template>
  <div>
    <!-- Desktop Gallery -->
    <div v-if="!isMobile" class="gallery-container">
      <h2 class="gallery-title">Galeria de Fotos</h2>
      <div v-for="section in gallerySections" :key="section.section" class="gallery-section">
        <h3 class="section-title">{{ section.section }}</h3>
        <div class="masonry-grid">
          <div
            v-for="(photo, index) in section.photos"
            :key="`${section.section}-${index}`"
            class="gallery-item"
            :class="{ 'gallery-item--portrait': isPortrait(photo) }"
            @click="openLightbox(allPhotos, getGlobalIndex(section, index))"
          >
            <div class="image-wrapper">
              <img 
                :src="photo.path" 
                :alt="photo.title" 
                class="gallery-image"
                loading="lazy"
                @load="onImageLoad($event, photo)"
              />
              <div class="image-overlay">
                <span class="image-title">{{ photo.title }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <vue-easy-lightbox
        ref="lightbox"
        :visible="lightboxVisible"
        :imgs="allPhotos.map(photo => photo.path)"
        :index="currentPhotoIndex"
        :moveDisabled="false"
        :pinchEnabled="true"
        :maxZoom="5"
        :minZoom="0.1"
        :zoomScale="0.3"
        :enableSwipe="true"
        scrollDisabled
        @hide="closeLightbox"
      >
        <template v-slot:toolbar>
          <div class="toolbar-custom">
            <v-btn icon @click="closeLightbox" class="white--text">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <div class="photo-title">{{ currentPhoto.title }}</div>
            <div class="navigation-buttons">
              <v-btn icon @click="previousPhoto" class="white--text">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn icon @click="nextPhoto" class="white--text">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn icon @click="currentZoom = Math.min(currentZoom * 1.5, 5)" class="white--text">
                <v-icon>mdi-magnify-plus</v-icon>
              </v-btn>
              <v-btn icon @click="currentZoom = Math.max(currentZoom / 1.5, 0.1)" class="white--text">
                <v-icon>mdi-magnify-minus</v-icon>
              </v-btn>
            </div>
          </div>
        </template>
      </vue-easy-lightbox>
    </div>

    <!-- Mobile Gallery -->
    <MobileGalleryComponent
      v-else
      :gallerySections="gallerySections"
    />
  </div>
</template>

<script>
import MobileGalleryComponent from './MobileGalleryComponent.vue'
import VueEasyLightbox from 'vue-easy-lightbox'

export default {
  name: "SectionGalleryComponent",
  
  components: {
    MobileGalleryComponent,
    VueEasyLightbox
  },

  props: {
    gallerySections: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  data() {
    return {
      lightboxVisible: false,
      currentPhotoIndex: 0,
      isMobile: false,
      imageAspectRatios: new Map(),
      currentZoom: 1
    }
  },

  computed: {
    allPhotos() {
      return this.gallerySections.flatMap(section => section.photos);
    },

    currentPhoto() {
      return this.allPhotos[this.currentPhotoIndex] || {};
    }
  },

  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  },

  watch: {
    currentZoom(newZoom) {
      const lightbox = this.$refs.lightbox;
      if (lightbox && lightbox.$el) {
        const img = lightbox.$el.querySelector('img');
        if (img) {
          img.style.transform = `scale(${newZoom})`;
        }
      }
    }
  },

  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 600
    },

    isPortrait(photo) {
      const aspectRatio = this.imageAspectRatios.get(photo.path);
      return aspectRatio ? aspectRatio < 1 : false;
    },

    onImageLoad(event, photo) {
      const img = event.target;
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      this.imageAspectRatios.set(photo.path, aspectRatio);
    },

    openLightbox(photos, index) {
      this.currentPhotoIndex = index;
      this.lightboxVisible = true;
    },

    previousPhoto() {
      if (this.currentPhotoIndex > 0) {
        this.currentPhotoIndex--;
      } else {
        this.currentPhotoIndex = this.allPhotos.length - 1;
      }
    },

    nextPhoto() {
      if (this.currentPhotoIndex < this.allPhotos.length - 1) {
        this.currentPhotoIndex++;
      } else {
        this.currentPhotoIndex = 0;
      }
    },

    getGlobalIndex(section, index) {
      const sectionIndex = this.gallerySections.findIndex(sec => sec === section);
      const photosBefore = this.gallerySections
        .slice(0, sectionIndex)
        .reduce((acc, sec) => acc + sec.photos.length, 0);
      return photosBefore + index;
    },

    closeLightbox() {
      this.lightboxVisible = false;
    },
  }
}
</script>

<style scoped>
.gallery-container {
  width: 100%;
  margin: 2rem auto;
  padding: 0;
  max-width: 1400px;
}

.gallery-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: var(--v-primary-base);
  padding: 0 1rem;
  text-align: left;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 2rem 0 1.5rem;
  color: var(--v-secondary-base);
  padding: 0 1rem;
  letter-spacing: -0.25px;
  line-height: 1.3;
}

.masonry-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-auto-rows: 0;
  grid-gap: 1rem;
  padding: 0 1rem;
}

.gallery-item {
  grid-row-end: span 26;
  break-inside: avoid;
  margin-bottom: 1rem;
  transition: transform 0.3s ease;
}

.gallery-item--portrait {
  grid-row-end: span 36;
}

.image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.7));
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1rem;
}

.gallery-item:hover .image-overlay {
  opacity: 1;
}

.gallery-item:hover .gallery-image {
  transform: scale(1.05);
}

.image-title {
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
}

.toolbar-custom {
  width: 100%;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
}

.photo-title {
  color: white;
  font-size: 1.1rem;
  margin: 0 1rem;
  flex-grow: 1;
  text-align: center;
}

.navigation-buttons {
  display: flex;
  gap: 0.5rem;
}

/* Lightbox Custom Styles */
:deep(.toolbar-custom) {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.8);
  z-index: 2000;
}

:deep(.photo-title) {
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0 1rem;
  text-align: center;
  flex: 1;
}

:deep(.navigation-buttons) {
  display: flex;
  gap: 0.5rem;
}

:deep(.vel-modal) {
  background: rgba(0, 0, 0, 0.9) !important;
}

:deep(.btn-close),
:deep(.btn-prev),
:deep(.btn-next) {
  display: none !important;
}

@media (max-width: 600px) {
  .gallery-container {
    margin: 1.5rem auto;
  }

  .gallery-title {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
    margin: 1.5rem 0 1rem;
  }

  .masonry-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    grid-gap: 0.75rem;
  }
  
  .gallery-item {
    grid-row-end: span 22;
  }
  
  .gallery-item--portrait {
    grid-row-end: span 32;
  }

  .image-title {
    font-size: 1rem;
  }
}
</style>